import { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { useSnackbar } from "notistack";
import Locale from "../lang/Locale";
import { reset } from "./authSlice";
import { resetErrorState } from "../error/errorHandlingSlice";
import { removeAccessToken } from "./authTokenService";
import { decrypt, isEmpty } from "../utils/helper";

const TYPE_MS = 38;
const ERASE_MS = 20;
const PAUSE_MS = 10_000;

export const TYPEWRITER_FIELDS = [
  { key: "login.leftHeading", light: true, tag: "h1", className: "ta-login__heading" },
  { key: "login.leftTagline", light: true, tag: "p", className: "ta-login__tagline" },
  { key: "login.headline", light: false, tag: "h2", className: "ta-login__headline" },
  { key: "login.sub", light: false, tag: "p", className: "ta-login__sub" },
  { key: "login.sso", light: false, tag: "span", className: "ta-login__sso-label" },
];

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export function getLoginLocale() {
  const lang = (navigator.language || "de").toLowerCase();
  return lang.startsWith("en") ? Locale.en : Locale.de;
}

function getStartLang() {
  return (navigator.language || "de").toLowerCase().startsWith("en") ? "en" : "de";
}

const emptyCopy = () =>
  Object.fromEntries(TYPEWRITER_FIELDS.map(({ key }) => [key, ""]));

/** Show blinking cursor on every field while a parallel type/erase step runs. */
export const TYPEWRITER_CURSOR_ALL = "__all__";

const textsForLang = (lang) =>
  Object.fromEntries(
    TYPEWRITER_FIELDS.map(({ key }) => [key, Locale[lang].messages[key] || ""])
  );

/** React-driven typewriter + DE/EN cycle (no direct DOM writes). */
export function useLoginTypewriter(setLocale) {
  const [copy, setCopy] = useState(() => emptyCopy());
  const [cursorKey, setCursorKey] = useState(null);
  const runIdRef = useRef(0);

  useEffect(() => {
    const runId = ++runIdRef.current;
    const active = () => runId === runIdRef.current;

    const typeAllFields = async (lang) => {
      const texts = textsForLang(lang);
      const maxLen = Math.max(0, ...Object.values(texts).map((t) => t.length));

      setCursorKey(TYPEWRITER_CURSOR_ALL);
      for (let i = 0; i <= maxLen; i += 1) {
        if (!active()) return;
        setCopy(() => {
          const next = {};
          TYPEWRITER_FIELDS.forEach(({ key }) => {
            next[key] = texts[key].slice(0, Math.min(i, texts[key].length));
          });
          return next;
        });
        if (TYPEWRITER_FIELDS.some(({ key }) => texts[key].length > i)) {
          await sleep(TYPE_MS);
        }
      }
      await sleep(400);
    };

    const eraseAllFields = async (lang) => {
      const texts = textsForLang(lang);
      const maxLen = Math.max(0, ...Object.values(texts).map((t) => t.length));

      setCursorKey(TYPEWRITER_CURSOR_ALL);
      for (let i = maxLen; i >= 0; i -= 1) {
        if (!active()) return;
        setCopy(() => {
          const next = {};
          TYPEWRITER_FIELDS.forEach(({ key }) => {
            next[key] = texts[key].slice(0, Math.min(i, texts[key].length));
          });
          return next;
        });
        if (TYPEWRITER_FIELDS.some(({ key }) => texts[key].length > i)) {
          await sleep(ERASE_MS);
        }
      }
    };

    const loop = async () => {
      let lang = getStartLang();
      setLocale(Locale[lang]);
      setCopy(emptyCopy());

      while (active()) {
        await typeAllFields(lang);
        setCursorKey(null);
        await sleep(PAUSE_MS);
        if (!active()) return;

        await eraseAllFields(lang);
        await sleep(80);
        if (!active()) return;

        lang = lang === "de" ? "en" : "de";
        setLocale(Locale[lang]);
      }
    };

    loop();

    return () => {
      runIdRef.current += 1;
      setCursorKey(null);
    };
  }, [setLocale]);

  return { copy, cursorKey };
}

/** Auth reset, errors, redirect after login. */
export function useLoginSession() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const userLogin = useSelector((s) => s.authentication);
  const errorState = useSelector((s) => s.errorhandling);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    dispatch(reset());
    removeAccessToken();
  }, [dispatch]);

  useEffect(() => {
    if (!errorState.hasErrors || !errorState.responseError) return;
    enqueueSnackbar(errorState.messages, {
      variant: "error",
      persist: false,
      preventDuplicate: true,
      autoHideDuration: 4000,
      anchorOrigin: { vertical: "bottom", horizontal: "center" },
    });
    dispatch(resetErrorState());
  }, [dispatch, enqueueSnackbar, errorState]);

  useEffect(() => {
    const dec = decrypt(userLogin.user);
    if (isEmpty(dec)) return;
    try {
      const user = JSON.parse(dec);
      if (user.loggedIn) navigate(location.state?.from ?? "/guru/");
    } catch {
      /* ignore malformed payload */
    }
  }, [navigate, location.state, userLogin.user]);
}
